// Caregiver reports — what was actually delivered, visit by visit. Until now
// the tasks a caregiver ticked, the note they wrote and the readings they took
// were recorded but shown nowhere in CAERA. This is the read surface for all
// three, plus a vital signs trend, and it prints for a records request.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { addDays, endOfWeek, format, isSameDay, parseISO, startOfWeek, subDays } from 'date-fns';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import InlineNote from '../InlineNote';
import { canEditNotes } from '../ActivityNote';
import { FIELDS, TEMP_SITES, POSITIONS, ACTIVITIES, bp, outOfRange, getClientVitals, getVisitVitals, recordVitals, hasAnyReading } from '../../lib/vitals';

const card = { background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 10, padding: 16, marginBottom: 14 };
const field = { width: '100%', padding: '7px 9px', borderRadius: 7, border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text)', fontSize: 12.5, fontFamily: 'inherit' };
const label = { fontSize: 10, color: 'var(--text3)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 };
const th = { textAlign: 'left', fontSize: 10, color: 'var(--text3)', fontWeight: 500, padding: '6px 8px', borderBottom: '1px solid var(--border)', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' };
const td = { padding: '6px 8px', borderBottom: '0.5px solid var(--border)', fontSize: 12.5, verticalAlign: 'top' };
const seg = (active) => ({ padding: '5px 12px', fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', border: 'none', background: active ? 'var(--accent)' : 'transparent', color: active ? 'var(--white)' : 'var(--text3)' });

const TASK_STATUS = {
  completed: { t: 'Done',      color: 'var(--success)', bg: 'var(--success-bg)' },
  refused:   { t: 'Refused',   color: 'var(--danger)',  bg: 'var(--danger-bg)' },
  not_done:  { t: 'Not done',  color: 'var(--danger)',  bg: 'var(--danger-bg)' },
  pending:   { t: 'Not ticked', color: 'var(--text3)',  bg: 'var(--bg2)' },
};
const EMPTY_VITALS = { temperature_f: '', temperature_site: '', pulse: '', respirations: '', bp_systolic: '', bp_diastolic: '', o2_saturation: '', weight_lb: '', pain_score: '', activity: '', position: '', note: '' };
const hhmm = (iso, tz) => { try { return format(parseISO(iso), 'h:mmaaa').replace(':00', ''); } catch { return '—'; } };

export default function CaregiverReports({ client }) {
  const { profile } = useAuth();
  const [view, setView] = useState('week');
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date(), { weekStartsOn: 0 }));
  const [visits, setVisits] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [vitals, setVitals] = useState([]);
  const [sigs, setSigs] = useState([]);
  const [trend, setTrend] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [entry, setEntry] = useState(null);
  const [busy, setBusy] = useState(false);

  const canRecord = ['admin', 'manager', 'nurse'].includes(profile?.role);
  const weekEnd = useMemo(() => endOfWeek(weekStart, { weekStartsOn: 0 }), [weekStart]);
  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    const from = new Date(weekStart); from.setHours(0, 0, 0, 0);
    const to = new Date(weekEnd); to.setHours(23, 59, 59, 999);
    const { data: vs, error: e1 } = await supabase.from('visits')
      .select(`id, scheduled_start, scheduled_end, actual_start, actual_end, status, timezone, care_note,
               staff:staff ( id, first_name, last_name, position_code ),
               service:service_catalog ( name, code )`)
      .eq('client_id', client.id)
      .gte('scheduled_start', from.toISOString()).lte('scheduled_start', to.toISOString())
      .order('scheduled_start');
    if (e1) setError(e1.message);
    const list = vs || [];
    setVisits(list);
    const ids = list.map(v => v.id);
    if (ids.length) {
      const [t, vt, sg] = await Promise.all([
        supabase.from('visit_tasks').select('id, visit_id, status, note, required, completed_at, task:task_catalog ( name, code, kind, capture )').in('visit_id', ids),
        getVisitVitals(ids),
        supabase.from('visit_signatures').select('id, visit_id, kind, signer_name, relationship, signed_at').in('visit_id', ids),
      ]);
      setTasks(t.data || []); setVitals(vt.data || []); setSigs(sg.data || []);
    } else { setTasks([]); setVitals([]); setSigs([]); }
    setLoading(false);
  }, [client.id, weekStart, weekEnd]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (view !== 'vitals') return;
    getClientVitals(client.id, subDays(new Date(), 180).toISOString()).then(({ data }) => setTrend(data || []));
  }, [view, client.id]);

  const byVisit = (arr) => arr.reduce((a, r) => { (a[r.visit_id] = a[r.visit_id] || []).push(r); return a; }, {});
  const tasksBy = useMemo(() => byVisit(tasks), [tasks]);
  const vitalsBy = useMemo(() => byVisit(vitals), [vitals]);
  const sigsBy = useMemo(() => byVisit(sigs), [sigs]);

  const totals = useMemo(() => {
    const done = tasks.filter(t => t.status === 'completed').length;
    return {
      visits: visits.filter(v => v.status === 'completed').length,
      notes: visits.filter(v => (v.care_note || '').trim()).length,
      tasks: `${done}/${tasks.length}`,
      open: tasks.filter(t => t.required && t.status === 'pending').length,
      vitals: vitals.length,
    };
  }, [visits, tasks, vitals]);

  // The visits trigger diffs every change into visit_events, so correcting a
  // note here keeps the caregiver's original wording in the visit history
  // rather than replacing it.
  const saveCareNote = async (visitId, text) => {
    const { error: e } = await supabase.from('visits').update({ care_note: text || null }).eq('id', visitId);
    if (e) throw new Error(e.message);
    await load();
  };

  const openEntry = (v) => setEntry({ visitId: v.id, when: v.scheduled_start, ...EMPTY_VITALS });
  const saveEntry = async () => {
    setBusy(true); setError('');
    try { await recordVitals(entry.visitId, entry); setEntry(null); await load(); }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
        <div style={{ display: 'flex', border: '0.5px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
          <button style={seg(view === 'week')} onClick={() => setView('week')}>By week</button>
          <button style={seg(view === 'vitals')} onClick={() => setView('vitals')}>Vital signs</button>
        </div>
        {view === 'week' && (
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <button className="btn btn-outline btn-sm" onClick={() => setWeekStart(d => addDays(d, -7))}>‹ Prev</button>
            <button className="btn btn-outline btn-sm" onClick={() => setWeekStart(startOfWeek(new Date(), { weekStartsOn: 0 }))}>This week</button>
            <button className="btn btn-outline btn-sm" onClick={() => setWeekStart(d => addDays(d, 7))}>Next ›</button>
            <span style={{ fontSize: 12.5, fontWeight: 600, marginLeft: 6 }}>{format(weekStart, 'd MMM')} – {format(weekEnd, 'd MMM yyyy')}</span>
            <button className="btn btn-outline btn-sm" onClick={() => window.print()} title="Print">🖨</button>
          </div>
        )}
      </div>

      {error && <div style={{ background: 'var(--danger-bg)', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: 'var(--danger)', marginBottom: 10 }}>{error}</div>}

      {view === 'vitals' ? (
        <VitalsTable rows={trend} />
      ) : loading ? (
        <p style={{ color: 'var(--text3)', fontSize: 12.5 }}>Loading…</p>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 8, marginBottom: 14 }}>
            {[['Visits worked', totals.visits], ['Tasks done', totals.tasks], ['Required open', totals.open], ['Care notes', totals.notes], ['Vitals taken', totals.vitals]].map(([l, n]) => (
              <div key={l} style={{ background: 'var(--bg2)', borderRadius: 8, padding: '9px 12px' }}>
                <div style={{ fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{l}</div>
                <div style={{ fontSize: 17, fontWeight: 600, color: l === 'Required open' && totals.open ? 'var(--warn)' : 'var(--text)' }}>{n}</div>
              </div>
            ))}
          </div>

          {days.map(d => {
            const dayVisits = visits.filter(v => isSameDay(parseISO(v.scheduled_start), d));
            return (
              <div key={d.toISOString()} style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 5 }}>
                  {format(d, 'EEEE d MMM')}
                </div>
                {dayVisits.length === 0 ? (
                  <div style={{ ...card, marginBottom: 0, padding: '10px 16px', color: 'var(--text3)', fontSize: 12 }}>No visit.</div>
                ) : dayVisits.map(v => (
                  <VisitReport key={v.id} v={v} tasks={tasksBy[v.id] || []} vitals={vitalsBy[v.id] || []}
                    sigs={sigsBy[v.id] || []} canRecord={canRecord} onRecord={() => openEntry(v)}
                    canEdit={canEditNotes(profile?.role)} onSaveNote={(text) => saveCareNote(v.id, text)} />
                ))}
              </div>
            );
          })}
        </>
      )}

      {entry && (
        <div className="modal-overlay" onMouseDown={e => e.target === e.currentTarget && setEntry(null)}>
          <div className="modal" onMouseDown={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
            <div className="modal-header"><span>Record vital signs</span><button className="btn btn-outline btn-sm" onClick={() => setEntry(null)}>Close</button></div>
            <div className="modal-body">
              <div style={{ fontSize: 11.5, color: 'var(--text3)', marginBottom: 12 }}>
                Visit of {format(parseISO(entry.when), 'EEE d MMM')}. Nurses record these in the app during the visit;
                entering them here is an office correction and is marked as one.
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                <div><div style={label}>Temp °F</div><input style={field} inputMode="decimal" value={entry.temperature_f} onChange={e => setEntry(f => ({ ...f, temperature_f: e.target.value }))} /></div>
                <div><div style={label}>Site</div><select style={field} value={entry.temperature_site} onChange={e => setEntry(f => ({ ...f, temperature_site: e.target.value }))}><option value="">—</option>{TEMP_SITES.map(s => <option key={s} value={s}>{s}</option>)}</select></div>
                <div><div style={label}>Pulse</div><input style={field} inputMode="numeric" value={entry.pulse} onChange={e => setEntry(f => ({ ...f, pulse: e.target.value }))} /></div>
                <div><div style={label}>Respirations</div><input style={field} inputMode="numeric" value={entry.respirations} onChange={e => setEntry(f => ({ ...f, respirations: e.target.value }))} /></div>
                <div><div style={label}>BP systolic</div><input style={field} inputMode="numeric" value={entry.bp_systolic} onChange={e => setEntry(f => ({ ...f, bp_systolic: e.target.value }))} /></div>
                <div><div style={label}>BP diastolic</div><input style={field} inputMode="numeric" value={entry.bp_diastolic} onChange={e => setEntry(f => ({ ...f, bp_diastolic: e.target.value }))} /></div>
                <div><div style={label}>SpO₂ %</div><input style={field} inputMode="numeric" value={entry.o2_saturation} onChange={e => setEntry(f => ({ ...f, o2_saturation: e.target.value }))} /></div>
                <div><div style={label}>Weight lb</div><input style={field} inputMode="decimal" value={entry.weight_lb} onChange={e => setEntry(f => ({ ...f, weight_lb: e.target.value }))} /></div>
                <div><div style={label}>Pain /10</div><input style={field} inputMode="numeric" value={entry.pain_score} onChange={e => setEntry(f => ({ ...f, pain_score: e.target.value }))} /></div>
                <div><div style={label}>Activity</div><select style={field} value={entry.activity} onChange={e => setEntry(f => ({ ...f, activity: e.target.value }))}><option value="">—</option>{ACTIVITIES.map(s => <option key={s} value={s}>{s}</option>)}</select></div>
                <div><div style={label}>Position</div><select style={field} value={entry.position} onChange={e => setEntry(f => ({ ...f, position: e.target.value }))}><option value="">—</option>{POSITIONS.map(s => <option key={s} value={s}>{s}</option>)}</select></div>
              </div>
              <div style={{ marginTop: 10 }}><div style={label}>Note</div><textarea style={{ ...field, minHeight: 54 }} value={entry.note} onChange={e => setEntry(f => ({ ...f, note: e.target.value }))} /></div>
            </div>
            <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn btn-outline" onClick={() => setEntry(null)}>Cancel</button>
              <button className="btn btn-primary" disabled={busy || (!hasAnyReading(entry) && !entry.note.trim())} onClick={saveEntry}>{busy ? 'Saving…' : 'Save readings'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function VisitReport({ v, tasks, vitals, sigs, canRecord, onRecord, canEdit, onSaveNote }) {
  const done = tasks.filter(t => t.status === 'completed').length;
  const openReq = tasks.filter(t => t.required && t.status === 'pending').length;
  const caregiver = v.staff ? `${v.staff.last_name}, ${v.staff.first_name}` : 'Unassigned';
  const worked = v.actual_start ? `${hhmm(v.actual_start)}–${v.actual_end ? hhmm(v.actual_end) : '…'}` : null;
  const cg = sigs.find(s => s.kind === 'caregiver');
  const cl = sigs.find(s => s.kind === 'client');

  return (
    <div style={card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', alignItems: 'baseline' }}>
        <div>
          <b style={{ fontSize: 13 }}>{caregiver}</b>
          {v.staff?.position_code && <span style={{ fontSize: 10.5, color: 'var(--text3)', marginLeft: 6 }}>{v.staff.position_code}</span>}
          <div style={{ fontSize: 11.5, color: 'var(--text3)', marginTop: 2 }}>
            {v.service?.name} · scheduled {hhmm(v.scheduled_start)}–{hhmm(v.scheduled_end)}
            {worked ? ` · worked ${worked}` : ' · not clocked'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ fontSize: 11.5, color: openReq ? 'var(--warn)' : 'var(--text3)' }}>
            {tasks.length ? `${done}/${tasks.length} tasks` : 'No checklist'}{openReq ? ` · ${openReq} required open` : ''}
          </span>
          {canRecord && v.actual_start && <button className="btn btn-outline btn-sm" style={{ fontSize: 10 }} onClick={onRecord}>+ Vitals</button>}
        </div>
      </div>

      {tasks.length > 0 && (
        <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 5 }}>
          {tasks.map(t => {
            const s = TASK_STATUS[t.status] || TASK_STATUS.pending;
            return (
              <span key={t.id} title={t.note || ''} style={{ fontSize: 11, padding: '3px 9px', borderRadius: 20, background: s.bg, color: s.color, border: '0.5px solid var(--border)' }}>
                {t.task?.name} · {s.t}{t.note ? ' ✎' : ''}
              </span>
            );
          })}
        </div>
      )}

      {tasks.some(t => t.note) && (
        <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text2)' }}>
          {tasks.filter(t => t.note).map(t => <div key={t.id}><b>{t.task?.name}:</b> {t.note}</div>)}
        </div>
      )}

      {vitals.length > 0 && (
        <div style={{ marginTop: 10, background: 'var(--bg2)', borderRadius: 8, padding: '8px 12px' }}>
          {vitals.map(x => (
            <div key={x.id} style={{ fontSize: 12, color: 'var(--text2)' }}>
              <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text3)' }}>{format(parseISO(x.recorded_at), 'h:mma')}</span>
              {' '}{FIELDS.filter(f => (f.key === 'bp' ? bp(x) : x[f.key]) != null).map(f => {
                const val = f.key === 'bp' ? bp(x) : x[f.key];
                const flag = outOfRange(x, f.key);
                return <span key={f.key} style={{ marginRight: 10, color: flag ? 'var(--warn)' : 'var(--text2)' }}>{f.label} <b>{val}</b>{f.unit === '%' ? '%' : ''}</span>;
              })}
              {x.position ? <span style={{ color: 'var(--text3)' }}>· {x.position}</span> : null}
              {x.source === 'office' && <span style={{ fontSize: 10, color: 'var(--text3)' }}> · entered by the office</span>}
              {x.note && <div style={{ color: 'var(--text3)', fontSize: 11.5 }}>{x.note}</div>}
            </div>
          ))}
        </div>
      )}

      {((v.care_note || '').trim() || canEdit) && (
        <div style={{ marginTop: 10 }}>
          <div style={label}>Care note</div>
          <InlineNote
            value={v.care_note}
            size={12.5}
            minHeight={110}
            placeholder="The caregiver wrote no note on this visit."
            canEdit={canEdit}
            editLabel="Correct this note. The caregiver's original is kept in the visit history."
            onSave={onSaveNote}
          />
        </div>
      )}

      <div style={{ marginTop: 10, fontSize: 11, color: 'var(--text3)' }}>
        {cg ? `Signed by ${cg.signer_name}` : 'No caregiver signature'}
        {' · '}
        {cl ? `client signed by ${cl.signer_name}${cl.relationship ? ` (${cl.relationship})` : ''}` : 'no client signature'}
      </div>
    </div>
  );
}

function VitalsTable({ rows }) {
  if (!rows.length) return <p style={{ fontSize: 12.5, color: 'var(--text3)' }}>No readings recorded in the last six months.</p>;
  return (
    <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr>
          {['Nurse', 'Date', 'Time', 'Temp', 'Pulse', 'BP', 'Resp', 'SpO₂', 'Weight', 'Pain', 'Activity', 'Position', 'Note'].map(h => <th key={h} style={th}>{h}</th>)}
        </tr></thead>
        <tbody>
          {rows.map(v => {
            const cell = (key, text) => {
              const flag = outOfRange(v, key);
              return <td style={{ ...td, color: flag ? 'var(--warn)' : 'var(--text2)', fontWeight: flag ? 600 : 400, whiteSpace: 'nowrap' }}>{text ?? '—'}</td>;
            };
            return (
              <tr key={v.id}>
                <td style={td}>{v.recorded_by_name || '—'}{v.source === 'office' && <div style={{ fontSize: 10, color: 'var(--text3)' }}>office entry</div>}</td>
                <td style={{ ...td, whiteSpace: 'nowrap' }}>{format(parseISO(v.recorded_at), 'd MMM yyyy')}</td>
                <td style={{ ...td, whiteSpace: 'nowrap' }}>{format(parseISO(v.recorded_at), 'h:mma')}</td>
                {cell('temperature_f', v.temperature_f != null ? `${v.temperature_f}°${v.temperature_site ? ` ${v.temperature_site[0].toUpperCase()}` : ''}` : null)}
                {cell('pulse', v.pulse)}
                {cell('bp_systolic', bp(v))}
                {cell('respirations', v.respirations)}
                {cell('o2_saturation', v.o2_saturation != null ? `${v.o2_saturation}%` : null)}
                {cell('weight_lb', v.weight_lb)}
                {cell('pain_score', v.pain_score)}
                <td style={td}>{v.activity || '—'}</td>
                <td style={td}>{v.position || '—'}</td>
                <td style={{ ...td, maxWidth: 220 }}>{v.note || '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
